import type React from "react";
import { Phone, MapPin } from "lucide-react";

const Footer: React.FC = () => {
  return (
    <footer
      style={{
        backgroundColor: "var(--color-altBrand)",
        color: "var(--color-text)",
      }}
      className="py-12"
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Address Section */}
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start mb-3">
              <MapPin className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Location</h3>
            </div>
            <div className="mb-3 rounded-lg overflow-hidden shadow-md max-w-[200px] mx-auto md:mx-0">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2468.8!2d-106.6426!3d52.1267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5304f6f6f6f6f6f6%3A0x6f6f6f6f6f6f6f6f!2s2710%20Lorne%20Ave%20%2320%2C%20Saskatoon%2C%20SK!5e0!3m2!1sen!2sca!4v1234567890!5m2!1sen!2sca"
                width="200"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Cycle Co Location"
              />
            </div>
            <p className="text-sm leading-relaxed">
              2710 Lorne Avenue Ste. 20-30
              <br />
              Saskatoon, SK S7S 0SJ
            </p>
          </div>

          {/* Contact Section */}
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start mb-3">
              <Phone className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Contact</h3>
            </div>
            <div className="space-y-1">
              <a
                href="mailto:info@cyclecollective.ca"
                className="block text-sm hover:opacity-70 transition-opacity"
              >
                info@cyclecollective.ca
              </a>
              <a
                href="tel:(639) 295-4557"
                className="block text-sm hover:opacity-70 transition-opacity"
              >
                (639) 295-4557
              </a>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-3">Follow Us</h3>
            <div className="flex flex-col items-center gap-3">
              {/* First Row - 3 icons */}
              <div className="flex gap-3">
                <a
                  href="//facebook.com/CycleCollectiveSaskatoon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  aria-label="Facebook"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="w-10 h-10 p-2 border-2 rounded-full"
                    style={{ color: "var(--color-brand)", borderColor: "var(--color-brand)" }}
                    fill="currentColor"
                  >
                    <title>Facebook</title>
                    <path fillRule="evenodd" d="M19 10.055C19 5.055 14.97 1 10 1s-9 4.054-9 9.055C1 14.574 4.29 18.32 8.594 19v-6.327H6.308v-2.618h2.286V8.059c0-2.269 1.344-3.523 3.4-3.523.984 0 2.014.177 2.014.177v2.228h-1.135c-1.117 0-1.467.698-1.467 1.415v1.699h2.496l-.399 2.618h-2.097V19C15.709 18.32 19 14.575 19 10.055" clipRule="evenodd" />
                  </svg>
                </a>
                <a
                  href="//www.instagram.com/cyclecosaskatoon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  aria-label="Instagram"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 30 30"
                    className="w-10 h-10 p-2 border-2 rounded-full"
                    style={{ color: "var(--color-brand)", borderColor: "var(--color-brand)" }}
                    fill="currentColor"
                  >
                    <title>Instagram</title>
                    <path d="M20.958 3h-12c-3.3 0-6 2.7-6 6v12c0 3.3 2.7 6 6 6h12c3.3 0 6-2.7 6-6V9c0-3.3-2.7-6-6-6m4 18c0 2.206-1.794 4-4 4h-12c-2.205 0-4-1.794-4-4V9c0-2.206 1.795-4 4-4h12c2.206 0 4 1.794 4 4z" />
                    <path d="M15 10a5 5 0 1 0 0 10 5 5 0 0 0 0-10m0 8c-1.654 0-3-1.346-3-3s1.346-3 3-3 3 1.346 3 3-1.346 3-3 3" />
                    <circle cx="21.417" cy="8.5" r="1.5" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@cyclecollectivesaskatoon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  aria-label="TikTok"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="w-10 h-10 p-2 border-2 rounded-full"
                    style={{ color: "var(--color-brand)", borderColor: "var(--color-brand)" }}
                    fill="currentColor"
                  >
                    <title>TikTok</title>
                    <path d="M18 5c-2.21 0-4-1.79-4-4h-3v12.5a2.5 2.5 0 0 1-5 0A2.5 2.5 0 0 1 8.5 11H9V8h-.5C5.46 8 3 10.46 3 13.5S5.46 19 8.5 19s5.5-2.46 5.5-5.5V6.74C15.13 7.53 16.51 8 18 8z" />
                  </svg>
                </a>
              </div>
              {/* Second Row - 2 icons, offset for stagger */}
              <div className="flex gap-3">
                <a
                  href="mailto:info@cyclecollective.ca"
                  className="hover:opacity-70 transition-opacity"
                  aria-label="Email"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="w-10 h-10 p-2 border-2 rounded-full"
                    style={{ color: "var(--color-brand)", borderColor: "var(--color-brand)" }}
                    fill="currentColor"
                  >
                    <title>Email</title>
                    <path d="M10 12.7c-.405 0-.809-.113-1.163-.339L2 8v6.5c0 .825.675 1.5 1.5 1.5h13c.825 0 1.5-.675 1.5-1.5V8l-6.839 4.362A2.15 2.15 0 0 1 10 12.7" />
                    <path d="M18 5.5c0-.825-.675-1.5-1.5-1.5h-13C2.675 4 2 4.675 2 5.5l8 5z" />
                  </svg>
                </a>
                <a
                  href="https://open.spotify.com/user/31zjnhdxfm3g5bh2qfpw4taf6ow4?si=731ac5e9319a4458"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  aria-label="Spotify"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="w-10 h-10 p-2 border-2 rounded-full"
                    style={{ color: "var(--color-brand)", borderColor: "var(--color-brand)" }}
                    fill="currentColor"
                  >
                    <title>Spotify</title>
                    <path d="M8.033 5.004c-.97-.022-2.042.048-3.11.204-.755.107-1.504.239-2.225.474-.548.178-.825.74-.642 1.236.192.523.759.793 1.316.634 1.382-.39 2.806-.532 4.239-.58.937-.031 1.869.027 2.796.12a19.3 19.3 0 0 1 3.34.62q1.391.39 2.656 1.068c.487.261 1.026.159 1.359-.235.337-.403.314-.899-.061-1.289-.136-.146-.324-.23-.501-.323a15.2 15.2 0 0 0-3.358-1.249 22.2 22.2 0 0 0-5.809-.68m-.084 3.642c-.389.009-.74.018-1.091.035a15.2 15.2 0 0 0-3.438.563c-.506.141-.754.58-.605 1.054.131.417.609.652 1.086.532.164-.04.329-.097.497-.133 1.864-.434 3.752-.491 5.649-.244 1.794.23 3.495.727 5.049 1.617a.9.9 0 0 0 1.19-.252c.272-.403.141-.922-.295-1.174a12 12 0 0 0-1.724-.833c-2.042-.789-4.164-1.156-6.318-1.165m-.455 3.482a20 20 0 0 0-3.7.469c-.404.089-.628.434-.544.78.089.372.431.576.853.5q.344-.06.689-.129c.937-.191 1.893-.275 2.848-.31.623-.022 1.246.013 1.864.075 1.078.106 2.113.35 3.11.745.483.191.932.439 1.396.669.332.164.745.045.913-.252.192-.337.089-.704-.253-.899a12 12 0 0 0-1.71-.806c-1.751-.687-3.587-.891-5.466-.842" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-black/20 pt-6">
          <div className="text-center space-y-2">
            <p className="text-sm">&copy; {new Date().getFullYear()} Cycle Collective Ltd.</p>
            <p className="text-xs text-black/70 max-w-2xl mx-auto">
              We use cookies to enable essential functionality on our website
              and analyze website traffic. By using our site, you consent to our
              use of cookies.{" "}
              <a
                href="/experience/privacypolicy"
                className="underline hover:opacity-70 transition-opacity"
              >
                Learn more about our privacy policy.
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
